import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRight, Send, Loader2, CheckCircle, AlertCircle, X, Mail, Phone, MapPin, MessageSquare, Code, Database, Smartphone, Globe, Layers, Terminal, Brain, Rocket, Megaphone, TrendingUp, LayoutDashboard, Lock, ShieldCheck } from 'lucide-react';
import { ParallaxBackground } from './ParallaxBackground';
import { useScroll } from 'framer-motion';
import { db, auth, handleFirestoreError, OperationType } from '../src/firebase';
import { useAuth } from '../src/contexts/AuthContext';
import { collection, addDoc } from 'firebase/firestore';

export const Contact: React.FC = () => {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    message: false,
    captcha: false
  });
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    captcha: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [consultationType, setConsultationType] = useState('saas_mvp');

  const consultationOptions = useMemo(() => [
    { id: 'apps_dashboards', label: 'Apps & Admin Dashboards', desc: 'Custom web/mobile apps & command centers', icon: LayoutDashboard },
    { id: 'portals_security', label: 'Login Portals & Security', desc: 'Secure client gateways, SSO, RBAC & auth', icon: ShieldCheck },
    { id: 'saas_mvp', label: 'SaaS & MVP', desc: 'SaaS platform or web MVP validation', icon: Layers },
    { id: 'social_ads', label: 'Social & Paid Ads', desc: 'Meta (FB/IG) & Google Ads ROAS scaling', icon: Megaphone },
    { id: 'ai_automation', label: 'AI & Automation', desc: 'AI agents & LLM integrations', icon: Brain },
    { id: 'architecture', label: 'System Design', desc: 'Scalable system technical audits', icon: Database },
    { id: 'general', label: 'Strategic Consulting', desc: 'Technical strategy or growth advisory', icon: TrendingUp }
  ], []);

  const { scrollY } = useScroll();

  const backgroundIcons = useMemo(() => [
    { Icon: Mail, top: '10%', left: '15%', size: 40, speed: 0.1, delay: 0.2, label: 'Mail - Contact' },
    { Icon: Megaphone, top: '18%', left: '75%', size: 46, speed: 0.14, delay: 0.3, label: 'Megaphone - Paid Ads' },
    { Icon: MessageSquare, top: '35%', left: '85%', size: 44, speed: 0.15, delay: 0.4, label: 'Chat - Support' },
    { Icon: Brain, top: '70%', left: '10%', size: 56, speed: 0.2, delay: 0.6, label: 'Brain - AI' },
    { Icon: Rocket, top: '80%', left: '85%', size: 48, speed: 0.25, delay: 0.8, label: 'Rocket - Launch' },
    { Icon: Code, top: '40%', left: '90%', size: 36, speed: 0.12, delay: 1.0, label: 'Code - Dev' },
    { Icon: Terminal, top: '50%', left: '5%', size: 42, speed: 0.18, delay: 1.2, label: 'Terminal - Ops' },
  ], []);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 40, stiffness: 200, mass: 0.5 };
  const mouseX = useSpring(x, springConfig);
  const mouseY = useSpring(y, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      x.set((e.clientX / window.innerWidth) - 0.5);
      y.set((e.clientY / window.innerHeight) - 0.5);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, y]);

  const bgX = useTransform(mouseX, [-0.5, 0.5], ["-30px", "30px"]);
  const bgY = useTransform(mouseY, [-0.5, 0.5], ["-30px", "30px"]);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const n1 = Math.floor(Math.random() * 10) + 1;
    const n2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({
      num1: n1,
      num2: n2,
      answer: ''
    });
    setFieldErrors(prev => ({ ...prev, captcha: '' }));
  };

  const validateField = (field: string, value: string) => {
    let errMsg = '';
    if (field === 'name') {
      if (!value.trim()) errMsg = 'Name is required';
    } else if (field === 'email') {
      if (!value.trim()) {
        errMsg = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errMsg = 'Please enter a valid email address';
      }
    } else if (field === 'phone') {
      if (value.trim() && !/^\+?[\d\s-]{7,15}$/.test(value)) {
        errMsg = 'Please enter a valid phone number';
      }
    } else if (field === 'message') {
      if (!value.trim()) errMsg = 'Message is required';
    } else if (field === 'captcha') {
      if (!value.trim()) {
        errMsg = 'Answer is required';
      } else if (parseInt(value) !== captcha.num1 + captcha.num2) {
        errMsg = 'Incorrect math answer';
      }
    }
    setFieldErrors(prev => ({ ...prev, [field]: errMsg }));
    return errMsg === '';
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'captcha') {
      validateField(field, captcha.answer);
    } else {
      validateField(field, formData[field as keyof typeof formData]);
    }
  };

  const handleChange = (field: string, value: string) => {
    if (field === 'captcha') {
      setCaptcha(prev => ({ ...prev, answer: value }));
      validateField('captcha', value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      validateField(field, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check for spam bots
    if (honeypot) {
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTouched({ name: false, email: false, phone: false, message: false, captcha: false });
      return;
    }

    setTouched({ name: true, email: true, phone: true, message: true, captcha: true });
    
    const isNameValid = validateField('name', formData.name);
    const isEmailValid = validateField('email', formData.email);
    const isPhoneValid = validateField('phone', formData.phone);
    const isMessageValid = validateField('message', formData.message);
    const isCaptchaValid = validateField('captcha', captcha.answer);

    if (!isNameValid || !isEmailValid || !isPhoneValid || !isMessageValid || !isCaptchaValid) {
      setError('Please fix the errors below before submitting.');
      if (!isCaptchaValid && captcha.answer) generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const user = auth.currentUser;
      const selectedOpt = consultationOptions.find(o => o.id === consultationType);
      const categoryLabel = selectedOpt ? selectedOpt.label : 'General';
      const timestamp = new Date().toISOString();

      const messageData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        content: `Consultation Segment: ${categoryLabel}\n\nClient Message:\n${formData.message.trim()}`,
        createdAt: timestamp,
        status: 'unread',
        senderId: user?.uid || null,
        consultationType: consultationType,
        subject: `${categoryLabel} Lead: ${formData.name.trim()}`
      };

      let messageSaved = false;

      // 1. Save to messages for the standard Inbound view
      try {
        await addDoc(collection(db, 'messages'), messageData);
        messageSaved = true;
      } catch (err) {
        console.warn('Inbound messages collection write error:', err);
      }

      // 2. Also save to tickets
      try {
        const clientId = user ? user.uid : `anonymous-${formData.email.trim().replace(/[^a-zA-Z0-9]/g, '_')}`;
        await addDoc(collection(db, 'tickets'), {
          clientId: clientId,
          clientEmail: formData.email.trim(),
          clientName: formData.name.trim(),
          clientPhone: formData.phone.trim(),
          subject: `${categoryLabel} Lead: ${formData.name.trim()}`,
          category: 'general',
          description: `Consultation Interest Focus: ${categoryLabel}\n\nClient Message:\n${formData.message.trim()}`,
          status: 'new',
          priority: 'medium',
          createdAt: timestamp,
          updatedAt: timestamp,
          tenantId: profile?.tenantId || ''
        });
        messageSaved = true;
      } catch (err) {
        console.warn('Tickets collection write notice:', err);
      }

      // 3. Store in localStorage as guaranteed fallback
      try {
        const localMessages = JSON.parse(localStorage.getItem('inbound_messages') || '[]');
        localMessages.push({ ...messageData, id: `local_${Date.now()}` });
        localStorage.setItem('inbound_messages', JSON.stringify(localMessages.slice(-50)));
        messageSaved = true;
      } catch (storageErr) {
        console.warn('Local backup warning:', storageErr);
      }

      if (messageSaved) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setConsultationType('saas_mvp');
        setCaptcha(prev => ({ ...prev, answer: '' }));
        setTouched({ name: false, email: false, phone: false, message: false, captcha: false });
        generateCaptcha();
      } else {
        setError('Failed to send message. Please check your connection and try again.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-black/90 text-white border-t border-white/5 relative overflow-hidden">
      <ParallaxBackground 
        icons={backgroundIcons} 
        mouseX={mouseX} 
        mouseY={mouseY} 
        scrollY={scrollY} 
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        
        <div className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-mono text-[#d8aa68] uppercase tracking-widest mb-3 block">
              How To Contact Me
            </span>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-display font-bold tracking-tight text-white mb-4 leading-[1.1] uppercase px-4">
              Ready to <br className="sm:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffe0a3] via-[#d8aa68] to-[#ffd885]">
                Architect Viability?
              </span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
              Whether you need custom web &amp; mobile apps, a high-control admin dashboard, a secure customer login portal with enterprise RBAC security, or full-scale venture architecture — send a message and let's build it.
            </p>
        </div>

        {/* Main Message Form Container */}
        <div id="contact-form" className="relative w-full">
          <motion.div 
            style={{ x: bgX, y: bgY }}
            className="absolute inset-0 bg-gradient-to-b from-[#4d2714]/25 via-transparent to-transparent rounded-3xl blur-3xl -z-10" 
          />

          <div className="bg-[#120703]/85 border border-[#d8aa68]/30 rounded-3xl p-5 sm:p-8 md:p-10 backdrop-blur-md shadow-[0_20px_50px_rgba(0,0,0,0.95)]">
            <AnimatePresence>
              {isSuccess && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95, y: -15 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -15 }}
                  className="bg-[#241006] border border-[#d8aa68] rounded-2xl p-6 relative overflow-hidden mb-8 shadow-[0_0_30px_rgba(216,170,104,0.3)]"
                >
                  <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-[#ffd885] to-[#d8aa68]"></div>
                  <button 
                    onClick={() => setIsSuccess(false)}
                    className="absolute top-4 right-4 text-[#ffe0a3] hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
                    aria-label="Dismiss message"
                  >
                    <X size={20} />
                  </button>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#4d2714] p-3 rounded-full text-[#ffd885] shrink-0 border border-[#d8aa68]/60 shadow-lg">
                      <CheckCircle size={28} />
                    </div>
                    <div>
                      <h4 className="text-[#ffe0a3] font-bold text-xl mb-1">Message Received!</h4>
                      <p className="text-gray-200 text-sm sm:text-base leading-relaxed">
                        Thank you for reaching out. Your message and project details have been recorded securely. I will review and get back to you directly within 24–48 hours.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4">
              <div>
                <h3 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">Send a Message</h3>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">Fill out the details below to initiate direct consultation.</p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span>Active Inquiry Channel</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-mono text-[#ecd8be] uppercase tracking-widest mb-2 font-semibold">
                    Your Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      maxLength={100}
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      className={`w-full bg-black/60 border ${fieldErrors.name && touched.name ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : touched.name && !fieldErrors.name ? 'border-[#d8aa68]/60 shadow-[0_0_10px_rgba(216,170,104,0.15)]' : 'border-white/15'} rounded-xl p-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#ffd885] transition-all duration-300`}
                      placeholder="e.g. Alex Morgan"
                    />
                    {touched.name && !fieldErrors.name && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ffd885]">
                        <CheckCircle size={18} />
                      </div>
                    )}
                  </div>
                  {fieldErrors.name && touched.name && (
                    <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle size={12} /> {fieldErrors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-mono text-[#ecd8be] uppercase tracking-widest mb-2 font-semibold">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={`w-full bg-black/60 border ${fieldErrors.email && touched.email ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : touched.email && !fieldErrors.email ? 'border-[#d8aa68]/60 shadow-[0_0_10px_rgba(216,170,104,0.15)]' : 'border-white/15'} rounded-xl p-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#ffd885] transition-all duration-300`}
                      placeholder="alex@enterprise.com"
                    />
                    {touched.email && !fieldErrors.email && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ffd885]">
                        <CheckCircle size={18} />
                      </div>
                    )}
                  </div>
                  {fieldErrors.email && touched.email && (
                    <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle size={12} /> {fieldErrors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-mono text-[#ecd8be] uppercase tracking-widest mb-2 font-semibold">
                  Phone / WhatsApp <span className="text-gray-400 font-normal text-[10px]">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    onBlur={() => handleBlur('phone')}
                    className={`w-full bg-black/60 border ${fieldErrors.phone && touched.phone ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : touched.phone && !fieldErrors.phone ? 'border-[#d8aa68]/60 shadow-[0_0_10px_rgba(216,170,104,0.15)]' : 'border-white/15'} rounded-xl p-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#ffd885] transition-all duration-300`}
                    placeholder="+1 (555) 234-5678"
                  />
                  {touched.phone && !fieldErrors.phone && formData.phone && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ffd885]">
                      <CheckCircle size={18} />
                    </div>
                  )}
                </div>
                {fieldErrors.phone && touched.phone && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle size={12} /> {fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-mono text-[#ecd8be] uppercase tracking-widest mb-2 font-semibold">
                  Project Scope & Message <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    maxLength={2000}
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    onBlur={() => handleBlur('message')}
                    rows={5}
                    className={`w-full bg-black/60 border ${fieldErrors.message && touched.message ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : touched.message && !fieldErrors.message ? 'border-[#d8aa68]/60 shadow-[0_0_10px_rgba(216,170,104,0.15)]' : 'border-white/15'} rounded-xl p-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#ffd885] transition-all duration-300 resize-none`}
                    placeholder="Describe your current system, target goals, timeline, or business challenge..."
                  />
                  {touched.message && !fieldErrors.message && (
                    <div className="absolute right-4 top-4 text-[#ffd885]">
                      <CheckCircle size={18} />
                    </div>
                  )}
                </div>
                {fieldErrors.message && touched.message && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle size={12} /> {fieldErrors.message}</p>
                )}
              </div>

              {/* Honeypot field - hidden from real users */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Simple Math CAPTCHA */}
              <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="captcha" className="block text-xs font-mono text-[#ecd8be] uppercase tracking-widest font-semibold">
                    Human Verification <span className="text-red-400">*</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={generateCaptcha}
                    className="text-[11px] font-mono text-[#ffe0a3] hover:text-white transition-colors flex items-center gap-1"
                  >
                    <Loader2 size={12} className={isSubmitting ? 'animate-spin' : ''} /> Refresh Math
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <span className="text-base font-mono font-bold bg-[#1f0f07] border border-[#d8aa68]/40 rounded-xl px-5 py-3 text-[#ffd885] text-center min-w-[120px] shadow-inner">
                    {captcha.num1} + {captcha.num2} = ?
                  </span>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      id="captcha"
                      value={captcha.answer}
                      onChange={(e) => handleChange('captcha', e.target.value)}
                      onBlur={() => handleBlur('captcha')}
                      className={`w-full bg-black/60 border ${fieldErrors.captcha && touched.captcha ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : touched.captcha && !fieldErrors.captcha && captcha.answer ? 'border-[#d8aa68]/60 shadow-[0_0_10px_rgba(216,170,104,0.15)]' : 'border-white/15'} rounded-xl p-3.5 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#ffd885] transition-all duration-300`}
                      placeholder="Enter result..."
                    />
                    {touched.captcha && !fieldErrors.captcha && captcha.answer && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ffd885]">
                        <CheckCircle size={18} />
                      </div>
                    )}
                  </div>
                </div>
                {fieldErrors.captcha && touched.captcha && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle size={12} /> {fieldErrors.captcha}</p>
                )}
              </div>

              {error && (
                <div className="text-red-300 text-sm bg-red-950/40 p-4 rounded-xl border border-red-500/30 flex items-start gap-3">
                  <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-400" />
                  <p>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 text-[#ffe0a3] border border-[#d8aa68]/60 font-bold text-sm uppercase tracking-widest hover:border-[#ffd885] hover:text-white transition-all rounded-xl flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_25px_rgba(20,8,3,0.8)] cursor-pointer"
                style={{
                  background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin text-[#ffd885]" />
                    <span>Transmitting Message...</span>
                  </>
                ) : (
                  <>
                    <span>Send Message Directly</span>
                    <Send size={16} className="text-[#ffd885]" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

      </div>
    </section>
  );
};
